import {
  Button,
  DatePicker,
  Dropdown,
  message,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Table,
  Tag,
  Typography,
} from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import { T_AffiliateApplication } from "api/user.service";
import PageTitle from "components/PageTitle";
import { loaderSm } from "components/QueryWrapper";
import TextNumeric from "components/TextNumeric";
import { DISBURSEMENT_STATUS, LEDGER_CATEGORY, LedgerSchemaDto } from "dtos/ledger.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useState } from "react";
import { TbAdjustments, TbCircleCheck, TbDots, TbTable } from "react-icons/tb";
import { Link, useParams } from "react-router-dom";
import { useAppSelector } from "store";
import { formatDateTime, formatMoney } from "utils/functions";
import { queryClient } from "utils/queryClient";
import dayjs from "dayjs";

const columns: ColumnsType<T_AffiliateApplication> = [
  {
    title: "Client",
    key: "client_details.name",
    render: (_, record) => {
      return (
        <Typography.Text italic={record?.client?.length <= 0}>
          <Link to={`/dashboard/application/${record?.app_key}`}>
            {record?.client?.length <= 0 ? "Untitled Client" : record?.client}
          </Link>
        </Typography.Text>
      );
    },
  },
  {
    title: "Status",
    key: "status.last_step",
    render: (_, record) => {
      return (
        <Tag color={record?.last_step === "onboarding" ? "success" : "default"}>
          {record?.last_step?.toUpperCase()?.split("_")?.join(" ") ?? "-"}
        </Tag>
      );
    },
  },
  {
    title: "Total Debt",
    key: "summary.current_debt",
    render: (_, record) => {
      return <Typography.Text>{record?.total_debt ? formatMoney(record?.total_debt) : "-"}</Typography.Text>;
    },
  },
  {
    title: "Has Lawsuits",
    key: "lawsuits",
    render: (_, record) => {
      return <Typography.Text>{record?.has_lawsuits ? "Yes" : "No"}</Typography.Text>;
    },
  },
  {
    title: "Strategy",
    key: "strategy.plan",
    render: (_, record) => {
      return <Typography.Text>{record?.strategy}</Typography.Text>;
    },
  },
  {
    title: "Initial Payment",
    key: "strategy.initial_payment",
    render: (_, record) => {
      return <Typography.Text>{formatMoney(record?.initial_payment)}</Typography.Text>;
    },
  },
  {
    title: "Initial Commision",
    key: "initial_commision",
    render: (_, record) => {
      // if (record?.last_step === "onboarding")
      return <Typography.Text>{formatMoney(record?.initial_commission)}</Typography.Text>; // Todo: needs to be fixed
      // return "-";
    },
  },
  {
    title: "Weekly Payment",
    key: "strategy.weekly_payments",
    render: (_, record) => {
      return <Typography.Text>{formatMoney(record?.weekly_payment)}</Typography.Text>;
    },
  },
  {
    title: "Weekly Commision",
    key: "weekly_commision",
    render: (_, record) => {
      // if (record?.last_step === "onboarding")
      return <Typography.Text>{formatMoney(record?.weekly_commission)}</Typography.Text>;
      // return "-";
    },
  },
  {
    title: "Payment Table",
    key: "payment_table",
    render: (_, record) => {
      return <PaymentTable appId={record?._id} />; // Todo: should use onboarding_completed which needs to be sent from backend
      // record?.last_step === "onboarding" ?
    },
  },
];

function PaymentTable({ appId }: { appId: string }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <Button icon={<TbTable />} onClick={() => setShowModal(true)} />
      {showModal ? <PaymentTableModal open={showModal} onClose={() => setShowModal(false)} appId={appId} /> : null}
    </>
  );
}

const DISBURSEMENT_STATUS_COLOR: Record<DISBURSEMENT_STATUS, string> = {
  [DISBURSEMENT_STATUS.NONE]: "default",
  [DISBURSEMENT_STATUS.UNPAID]: "volcano",
  [DISBURSEMENT_STATUS.PAID]: "blue",
  [DISBURSEMENT_STATUS.APPROVED]: "green",
  [DISBURSEMENT_STATUS.CREDITED]: "cyan",
  [DISBURSEMENT_STATUS.WITHDRAWN]: "gold",
  [DISBURSEMENT_STATUS.REJECTED]: "red",
  [DISBURSEMENT_STATUS.PROCESSING]: "processing",
};

const getPaymentTableColumns = ({
  affiliateId,
  approvePayment,
  changeStatusAffiliate,
  appId,
  setApproveModal,
  setStatusModal,
}: {
  affiliateId: string;
  approvePayment: ReturnType<typeof ApiService.ledger.useMarkPaymentAffiliate>;
  changeStatusAffiliate: ReturnType<typeof ApiService.ledger.useModifyStatusAffiliate>;
  appId: string;
  setApproveModal: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      week?: number;
      disbursementDate?: Date | null;
    }>
  >;
  setStatusModal: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      week?: number;
      status?: DISBURSEMENT_STATUS;
    }>
  >;
}): ColumnsType<LedgerSchemaDto> => [
  {
    title: "Sl.",
    dataIndex: "week",
    key: "week",
    width: 60,
    render: (_, record) => {
      return record?.week === 0 ? "Initial" : record.week / (record.category === LEDGER_CATEGORY.MONTHLY ? 4 : 1);
    },
  },
  {
    title: "Booking Date",
    dataIndex: "posting_date",
    key: "posting_date",
    width: 200,
    render: (_, record) => {
      const finalDate = record?.posting_date
        ? formatDateTime(record?.posting_date)
        : record?.order_date
          ? formatDateTime(record?.order_date)
          : "-";

      return (
        <TextNumeric>
          <Typography.Text type="secondary" className="text-xs">
            {finalDate}
          </Typography.Text>
        </TextNumeric>
      );
    },
  },
  {
    title: "Program Fee",
    dataIndex: "program_fee",
    key: "program_fee",
    width: 150,
    render: (_, record) => {
      return <>{formatMoney(record?.distribution?.program_fee)}</>;
    },
  },
  // {
  //   title: "Service Fee",
  //   dataIndex: "service_fee",
  //   key: "service_fee",
  //   render: (_, record) => {
  //     return <>{formatMoney(record?.distribution?.service_fee)}</>;
  //   },
  // },
  // {
  //   title: "Legal Fee",
  //   dataIndex: "legal_fee",
  //   key: "legal_fee",
  //   render: (_, record) => {
  //     return <>{formatMoney(record?.distribution?.legal_fee)}</>;
  //   },
  // },
  {
    title: "Affiliate Commission",
    // dataIndex: "legal_fee",
    key: "affiliate",
    width: 160,
    render: (_, record, index) => {
      //changing to multi-level find
      const latest = record?.affiliates.find((a) => a.affiliate_id.toString() === affiliateId);
      return latest?.amount ? formatMoney(latest?.amount) : "-";
      // return <>{formatMoney(record?.distribution?.program_fee * (index === 0 ? 0.1 : 0.05))}</>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 150,
    render: (_, record) => {
      //changing to multi-level find
      const latest = record?.affiliates.find((a) => a.affiliate_id.toString() === affiliateId);
      const status = latest?.status ?? DISBURSEMENT_STATUS.NONE;

      return <Tag color={DISBURSEMENT_STATUS_COLOR[status]}>{status.toUpperCase()}</Tag>;
    },
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    width: 75,
    align: "center",
    render: (_, record) => {
      const menuItems: ItemType[] = [
        {
          key: "approve-payment",
          icon: <TbCircleCheck color="green" />,
          label: "Approve Payment",
          onClick: () => {
            setApproveModal({
              open: true,
              week: record.week,
            });
          },
        },
        {
          key: "modify-status",
          icon: <TbAdjustments />,
          label: "Modify Status",
          onClick: () => {
            setStatusModal({
              open: true,
              week: record.week,
            });
          },
        },
      ];

      return (
        <Dropdown menu={{ items: menuItems }}>
          <Button icon={<TbDots />} />
        </Dropdown>
      );
    },
  },
];

function PaymentTableModal({
  open,
  onClose,
  appId,
}: {
  open: boolean;
  onClose: () => void;
  appId: string | undefined;
}) {
  const params = useParams();
  const currUser = useAppSelector((state) => state.user);
  const affiliateId = params?.id ?? currUser?._id;
  const [approveModal, setApproveModal] = useState<{
    open: boolean;
    week?: number;
    disbursementDate?: Date | null;
  }>({ open: false });

  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    week?: number;
    status?: DISBURSEMENT_STATUS;
  }>({ open: false });

  if (!appId || !affiliateId) return;

  const paymentTableQuery = ApiService.application.useGetPaymentTable(appId);
  const approvePayment = ApiService.ledger.useMarkPaymentAffiliate();
  const changeStatusAffiliate = ApiService.ledger.useModifyStatusAffiliate();

  const paymentTableColumns = getPaymentTableColumns({
    affiliateId,
    approvePayment,
    changeStatusAffiliate,
    appId,
    setApproveModal,
    setStatusModal,
  });

  return (
    <Modal open={open} onCancel={onClose} title="Payment Table" width={800} footer={null}>
      <Table
        loading={{ spinning: paymentTableQuery.isPending, indicator: loaderSm }}
        columns={paymentTableColumns}
        dataSource={paymentTableQuery?.data?.payment_table.filter((row) => row.category !== LEDGER_CATEGORY.SKIP)}
        summary={() => {
          const rows = paymentTableQuery.data?.payment_table ?? [];
          const totalProgramFee = rows.reduce((prev, curr) => curr.distribution.program_fee + prev, 0);
          const totalAffiliateFee = rows.reduce(
            (prev, curr) => curr.affiliates.find((af) => af.affiliate_id === affiliateId)?.amount ?? 0 + prev,
            0,
          );
          const bookingDate = rows.at(-1)?.posting_date || rows.at(-1)?.order_date;

          return (
            <Table.Summary fixed="bottom">
              <Table.Summary.Cell index={0} className="bg-gray-100 font-bold text-gray-800">
                Total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="left" className="bg-gray-100 font-bold text-gray-800">
                {bookingDate && (
                  <TextNumeric>
                    <Typography.Text type="secondary" className="text-xs">
                      {formatDateTime(bookingDate)}
                    </Typography.Text>
                  </TextNumeric>
                )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="left" className="bg-gray-100 font-bold text-gray-800">
                ${totalProgramFee}
              </Table.Summary.Cell>
              <Table.Summary.Cell
                index={3}
                align="left"
                className={`bg-gray-100 font-bold ${totalAffiliateFee ? "text-gray-800" : "text-gray-500"}`}
              >
                ${totalAffiliateFee}
              </Table.Summary.Cell>
              <Table.Summary.Cell
                index={4}
                align="left"
                className="bg-gray-100 font-bold text-gray-800"
              ></Table.Summary.Cell>
            </Table.Summary>
          );
        }}
        pagination={false}
        scroll={{ y: 500, x: 700 }}
        virtual
        bordered
        size="small"
      />
      <Modal
        open={approveModal.open}
        title="Approve Affiliate Payment"
        onCancel={() => setApproveModal({ open: false })}
        footer={null}
      >
        <div className="mt-4 flex flex-col gap-3">
          <DatePicker
            className="w-full"
            placeholder="Disbursement Date (optional)"
            value={approveModal.disbursementDate ? dayjs(approveModal.disbursementDate) : null}
            onChange={(date) =>
              setApproveModal((p) => ({
                ...p,
                disbursementDate: date ? date.toDate() : null,
              }))
            }
          />

          <Popconfirm
            title="Approve this affiliate payment?"
            placement="bottomRight"
            onConfirm={() => {
              approvePayment.mutate(
                {
                  body: {
                    id: appId,
                    affiliate_id: affiliateId,
                    idx: approveModal.week!,
                    disbursement_date: approveModal.disbursementDate?.toISOString(),
                  },
                },
                {
                  onSuccess: () => {
                    message.success("Affiliate payment approved successfully.");
                    queryClient.invalidateQueries({
                      queryKey: ["application/payment-table", appId],
                    });
                    setApproveModal({ open: false });
                  },
                },
              );
            }}
          >
            <Button type="primary" icon={<TbCircleCheck />}>
              Approve Payment
            </Button>
          </Popconfirm>
        </div>
      </Modal>
      <Modal
        open={statusModal.open}
        title="Modify Disbursement Status"
        onCancel={() => setStatusModal({ open: false })}
        footer={null}
      >
        <Select
          className="w-full"
          placeholder="Select disbursement status"
          value={statusModal.status}
          onChange={(value) => setStatusModal((p) => ({ ...p, status: value }))}
          options={Object.values(DISBURSEMENT_STATUS)
            .filter((s) => s !== DISBURSEMENT_STATUS.APPROVED)
            .map((status) => ({
              label: status.toUpperCase(),
              value: status,
            }))}
        />

        <div className="mt-4 flex justify-end">
          <Button
            type="primary"
            disabled={!statusModal.status}
            onClick={() => {
              changeStatusAffiliate.mutate(
                {
                  body: {
                    id: appId,
                    affiliate_id: affiliateId,
                    idx: statusModal.week!,
                    disbursement_status: statusModal.status!,
                  },
                },
                {
                  onSuccess: () => {
                    message.success("Status updated successfully.");
                    queryClient.invalidateQueries({
                      queryKey: ["application/payment-table", appId],
                    });
                    setStatusModal({ open: false });
                  },
                },
              );
            }}
          >
            Update Status
          </Button>
        </div>
      </Modal>
    </Modal>
  );
}

function AffiliateItem() {
  const params = useParams();
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const fromParamAffiliateId = params?.id;
  const currUser = useAppSelector((state) => state.user);
  const affiliateId = fromParamAffiliateId ?? currUser?._id;

  const affiliateApplications = ApiService.user.useGetOneAffiliateApllications(affiliateId, filters);
  const affiliateStatistics = ApiService.user.useGetOneAffiliateStatistics(affiliateId);

  const statistics = affiliateStatistics?.data?.statistics;

  return (
    <DashboardLayout>
      <PageTitle
        title={`Affiliate Statistics`}
        pageTitle={
          fromParamAffiliateId
            ? [{ title: "Affiliates", link: `/dashboard/affiliates` }, { title: "Affiliate Statistics" }]
            : [{ title: "Affiliate Statistics" }]
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 m-4">
        {[
          {
            label: statistics?.totalApplicationsConverted,
            subLabel: `/${statistics?.totalApplications}`,
            desc: "Successfully Converted",
          },
          { label: formatMoney(statistics?.lastMonthCommission ?? 0), desc: "Last Month Earnings" },
          { label: formatMoney(statistics?.MTDCommission ?? 0), desc: "MTD Earnings" },
          { label: formatMoney(statistics?.thisMonthCommission ?? 0), desc: "This Month Earnings" },
          { label: formatMoney(statistics?.nextMonthCommission ?? 0), desc: "Min. Expected Next Month" },
        ].map((el) => {
          return (
            <div key={el.desc} className="p-4 rounded-lg border border-solid border-gray-200 flex flex-col gap-2">
              {affiliateStatistics?.data ? (
                <div className="inline-flex items-baseline">
                  <Typography.Title level={3} className="font-black !m-0">
                    {el.label}
                  </Typography.Title>
                  {el?.subLabel ? (
                    <Typography.Title level={4} type="secondary" className="font-extrabold !m-0">
                      {el.subLabel}
                    </Typography.Title>
                  ) : null}
                </div>
              ) : (
                <Skeleton.Button active />
              )}
              <Typography.Text type="secondary">{el.desc}</Typography.Text>
            </div>
          );
        })}
      </div>

      <Table
        columns={columns}
        dataSource={affiliateApplications?.data?.data}
        loading={{
          indicator: loaderSm,
          spinning: affiliateApplications.isPending,
        }}
        rowKey={"_id"}
        pagination={{
          total: affiliateApplications?.data?.totalObjects,
          current: filters.page,
          pageSize: filters.limit,
          onChange: (page, pageSize) => {
            setFilters({ page: page, limit: pageSize });
          },
        }}
      />
    </DashboardLayout>
  );
}

export default AffiliateItem;
